import { Mutex } from '@aws-amplify/core';
import { BleClient } from '@capacitor-community/bluetooth-le';
import axios, { AxiosError } from 'axios';
import {
  BundleData,
  BundleParser,
  fatfs,
  SLink,
  StartStreamResponse_t,
  StreamResponse_t,
  StreamStatus_t,
  StreamStatusResponse_t,
  TubeStatus_t,
} from 'foreman-slink';
import { throttle } from 'lodash';
import { Firmware } from 'src/models/firmware';
import { bleActions } from 'src/redux/ble/actions';
import {
  selectBleBundleFiles,
  selectBleCanLiveStreamFromController,
  selectBleConnectedDevice,
  selectBleIsLocked,
} from 'src/redux/ble/selectors';
import { BleControllerBundle, BleControllerDevice, BleControllerFile } from 'src/redux/ble/types';
import { logActions } from 'src/redux/log/actions';
import { selectLogActiveFileName } from 'src/redux/log/selectors';
import { store } from 'src/redux/store';
import { getFirmwareFile } from 'src/utils/api/getFirmware';
import { updateController } from 'src/utils/api/updateController';
import { v4 as uuid, v5 } from 'uuid';
import { ErrorCode } from '../utils/errors';
import { bleIds } from './constants';
import { BleControllerWriteBundleRequest, IBleControllerService } from './IBleControllerService';
import type { LiveJobLogService } from './LiveJobLogService';

const LOG_FILE_DIR = '/log/cbe';
const BUNDLE_FILE_DIR = '/bpf';
const BUNDLE_LOCAL_FILE_DIR = '/bpf_local';
const BUNDLE_INFO_FILENAME = 'info.txt';
const BUNDLE_UUID_NAMESPACE = '076a7dcd-6228-4857-befd-70d7e97d8aca';

interface ControllerReadDirResponse {
  result: fatfs.fResult;
  info: fatfs.fInfo;
}

/**
 * A service that handles communication with a controller device over BLE.
 * Used as a singleton throughout the app, so should not be instantiated directly.
 */
export class BleControllerService implements IBleControllerService {
  private static readonly WRITE_CHUNK_SIZE = 128;
  private static readonly WRITE_PACKET_SIZE = BleControllerService.WRITE_CHUNK_SIZE * 16;
  private readonly slink: SLink;
  private readonly writeLock = new Mutex();

  private get activeLogFilename(): string | undefined {
    return selectLogActiveFileName(store.getState());
  }

  private get activeLogService(): LiveJobLogService | undefined {
    if (this.activeLogFilename === undefined) return undefined;
    return this.getJobLogService(this.activeLogFilename);
  }

  /**
   * Keeps track of whether the application is using the BLE device.
   * Only one interaction with the BLE device is allowed at a time.
   */
  private get lock() {
    return selectBleIsLocked(store.getState());
  }

  constructor(
    private readonly device: BleControllerDevice,
    public readonly getJobLogService: (filename: string) => LiveJobLogService | undefined,
    public readonly addJobLogService: (
      filename: string,
      serialNumber: string,
      skipMetadataCollection?: boolean,
    ) => LiveJobLogService,
  ) {
    this.slink = new SLink(async (data: Uint8Array) => {
      const release = await this.writeLock.acquire();
      try {
        for (let i = 0; i < data.byteLength; i += BleControllerService.WRITE_CHUNK_SIZE) {
          // We use data.slice (unfortunately) because BleClient doesn't seem to accept DataViews over the same buffer
          const chunk = data.slice(i, i + BleControllerService.WRITE_CHUNK_SIZE).buffer;
          await BleClient.writeWithoutResponse(device.deviceId, bleIds.uartService, bleIds.tx, new DataView(chunk));
        }
      } finally {
        release();
      }
    });

    if (this.activeLogService) {
      this.activeLogService.unpause();
    }
  }

  public readonly initialize = async (): Promise<BleControllerDevice> => {
    try {
      // Register all event listeners that should run for the lifetime of the SLink object
      this.slink.addListener('start-stream', this.onStreamStart);
      this.slink.addListener('stream-data', this.onStreamData);
      this.slink.addListener('stream-status', this.onStreamStatus);

      // Begin receiving events from the rx characteristic of the controller device
      await BleClient.startNotifications(this.device.deviceId, bleIds.uartService, bleIds.rx, (data: DataView) => {
        const uint8View = new Uint8Array(data.buffer);
        this.slink.parse(uint8View);
      });

      const initialSerialNumber = await this.slink.getSerialNumber(500);
      if (typeof initialSerialNumber !== 'string') throw new Error('Could not get serial number');
      const initialFirmware = await this.slink.getFWVersion(500);
      if (typeof initialFirmware !== 'string') throw new Error('Could not get firmware version');

      return {
        name: this.device.name,
        deviceId: this.device.deviceId,
        firmwareVersion: initialFirmware,
        serialNumber: initialSerialNumber,
      };
    } catch (error) {
      await this.close();
      throw error;
    }
  };

  public readonly deleteLog = async (filename: string) => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to delete log file while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      store.dispatch(bleActions.deleteLogFileStart());
      await this.closeControllerFileSystem();
      await this.slink.getFUnlink(`${LOG_FILE_DIR}/${filename}`);

      store.dispatch(bleActions.deleteLogFileSuccess());
    } catch (e) {
      console.error('error deleting log file', e);
      store.dispatch(bleActions.deleteLogFileFailure({ error: e instanceof Error ? e.message : undefined }));
    }
  };

  public readonly readLog = async (filename: string) => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to write log file while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      store.dispatch(bleActions.readLogFileStart({ filename }));
      const filepath = `${LOG_FILE_DIR}/${filename}`;

      const file = await this.readFile(filepath, (readBytes, totalBytes) => {
        store.dispatch(bleActions.readLogFileProgress({ readBytes, totalBytes }));
      });

      store.dispatch(bleActions.readLogFileSuccess({ data: file }));
    } catch (e) {
      console.error('error writing log file', e);
      store.dispatch(bleActions.readLogFileFailure({ error: e instanceof Error ? e.message : undefined }));
    }
  };

  public readonly writeLog = async (filename: string, data: string) => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to write log file while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      store.dispatch(bleActions.writeLogFileStart({ totalBytes: data.length }));
      const filePath = `${LOG_FILE_DIR}/${filename}`;

      await this.writeFile(filePath, data, writtenBytes => {
        store.dispatch(bleActions.writeLogFileProgress({ writtenBytes }));
      });

      store.dispatch(bleActions.writeLogFileSuccess());
    } catch (e) {
      console.error('error writing log file', e);
      store.dispatch(bleActions.writeLogFileFailure({ error: e instanceof Error ? e.message : undefined }));
    }
  };

  public readonly listLogFiles = async () => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to list log files while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      store.dispatch(bleActions.listLogFilesStart());

      const logFiles: BleControllerFile[] = await this.readDirectory(LOG_FILE_DIR);
      store.dispatch(bleActions.listLogFilesSuccess({ data: logFiles }));
    } catch (err) {
      console.error('Error reading log files:', err);
      store.dispatch(bleActions.listLogFilesFailure({ error: err instanceof Error ? err.message : undefined }));
    }
  };

  /**
   *
   * @param logFileName - The name of the log file.
   * @param siteName  - The name of the site the job was performed on
   * @param serialNumber - The serial number of the ctrl completing the job.
   * @param description - The description of the job.
   * @param hxModelNumber - The model number of the heat exchanger the job was performed on.
   * @param bundleId - The Id of the bundle downloaded to the controller.
   */
  public readonly syncLogFile = async (
    logFileName: string,
    siteName: string,
    serialNumber: string,
    description: string,
    hxModelNumber: string,
    bundleId?: string,
  ): Promise<void> => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to sync a log file while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      store.dispatch(bleActions.syncLogFileStart({ filename: logFileName }));

      const logFilePath = `${LOG_FILE_DIR}/${logFileName}`;

      const file = await this.readFile(
        logFilePath,
        throttle((readBytes, totalBytes) => {
          store.dispatch(bleActions.syncLogFileProgress({ uploadedBytes: readBytes, totalBytes: totalBytes }));
        }, 100),
      );

      const liveService = this.getJobLogService(logFileName) ?? this.addJobLogService(logFileName, serialNumber, true);

      store.dispatch(
        logActions.updateLogInfo({
          metadata: {
            description: description,
            heatExchangerModelNumber: hxModelNumber || undefined,
            siteName: siteName || undefined,
            bundleId: bundleId || undefined,
          },
          filename: logFileName,
        }),
      );

      await new Promise<void>((res, rej) => {
        try {
          liveService.onStreamData({
            payload_data: file,
            streamStatus: status => {
              if (status === StreamStatus_t.error) rej();
              else res();
            },
          });
        } catch (e) {
          rej(e);
        }
      });

      liveService.close();

      store.dispatch(bleActions.syncLogFileSuccess());
    } catch (err) {
      console.error('Error during syncLogFile:', err);
      store.dispatch(bleActions.syncLogFileFailure({ error: err instanceof Error ? err.message : undefined }));
      throw err;
    }
  };

  public readonly listBundles = async () => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to list bundles while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      store.dispatch(bleActions.listBundlesStart());

      await this.closeControllerFileSystem();
      await this.slink.getFChangeDirectory('/', 1000); // Start at root directory
      try {
        await this.slink.getFMakeDirectory(BUNDLE_FILE_DIR, 1000); // Create bundle directory if it does not exist
      } catch {}
      try {
        await this.slink.getFMakeDirectory(BUNDLE_LOCAL_FILE_DIR, 1000); // Create bundle local directory if it does not exist
      } catch {}

      const bundleDirFiles = await this.readDirectory(BUNDLE_FILE_DIR, true);
      bundleDirFiles.push(...(await this.readDirectory(BUNDLE_LOCAL_FILE_DIR, true)));

      const infoFiles = bundleDirFiles.filter(i => i.fileName === BUNDLE_INFO_FILENAME);
      const siteNames: Record<string, string> = {};
      // For each info file, load the site name from the controller.
      for (const file of infoFiles) {
        try {
          const data = await this.readFile(file.fullPath);
          if (data[0] === '!') {
            siteNames[file.dirPath] = data.slice(1);
          }
        } catch (err) {
          console.error(`Could not read file: ${file.fullPath}`, err);
        }
      }

      const bundleFiles: BleControllerBundle[] = bundleDirFiles
        .filter(i => i.fileName !== BUNDLE_INFO_FILENAME)
        .map(file => {
          const isSentinelBundle = !file.fullPath.startsWith(BUNDLE_LOCAL_FILE_DIR);
          const result: BleControllerBundle = {
            ...file,
            bundleId: isSentinelBundle ? file.fileName.replace('.csv', '') : uuid(),
            siteName: siteNames[file.dirPath] ?? file.dirPath.replace(/^\/bpf(?:_local)?\//gi, ''),
            isSentinelBundle,
          };
          return result;
        });

      store.dispatch(bleActions.listBundlesSuccess({ data: bundleFiles }));
    } catch (err) {
      console.error('Could not list bundles on the controller:', err);
      store.dispatch(bleActions.listBundlesFailure({ error: err instanceof Error ? err.message : undefined }));
      throw err;
    }
  };

  public readonly writeBundle = async (data: BleControllerWriteBundleRequest) => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to write a bundle while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      const bundleData: BundleData = {
        metadata: {
          is_rotated: data.isRotated,
          is_triangle: data.bundlePattern === 'triangle',
          name: data.name,
        },
        tubes: data.tubes.map(tube => {
          return {
            row: tube.row,
            column: tube.column,
            status: TubeStatus_t.Uncleaned, // For now, we always write bundle tubes as uncleaned. This may change in the future.
          };
        }),
      };
      const bundleDataStr = BundleParser.CreateBundleFile(bundleData);
      store.dispatch(bleActions.writeBundleStart({ bundleId: data.bundleId, totalBytes: bundleDataStr.length }));

      const sitePath = v5(data.siteName.replaceAll(/(?:(?![a-zA-Z0-9]).)+/gi, '-'), BUNDLE_UUID_NAMESPACE)
        .replaceAll('-', '')
        .substring(0, 16); // Generate a unique string from the site name.

      await this.closeControllerFileSystem();
      try {
        await this.slink.getFMakeDirectory(BUNDLE_FILE_DIR, 1000); // Create bundle directory if it does not exist
      } catch {}
      try {
        await this.slink.getFMakeDirectory(`${BUNDLE_FILE_DIR}/${sitePath}`, 1000); // Create site directory if it does not exist
      } catch {}

      const infoPath = `${BUNDLE_FILE_DIR}/${sitePath}/${BUNDLE_INFO_FILENAME}`;
      await this.writeFile(infoPath, `!${data.siteName}`); // Write the info.txt file to the site folder.

      const fileName = `${data.bundleId}.csv`;
      const dirPath = `${BUNDLE_FILE_DIR}/${sitePath}`;
      const filePath = `${dirPath}/${fileName}`;
      await this.writeFile(filePath, bundleDataStr, writtenBytes => {
        store.dispatch(bleActions.writeBundleProgress({ writtenBytes }));
      });

      const fileInfo: BleControllerBundle = {
        bundleId: data.bundleId,
        siteName: data.siteName,
        fileName,
        fullPath: filePath,
        dirPath,
        fileSize: bundleDataStr.length,
        createdAt: new Date().toISOString(),
        isSentinelBundle: true,
      };

      store.dispatch(bleActions.writeBundleSuccess({ data: fileInfo }));
    } catch (err) {
      console.error('Could not write bundle to controller:', err);
      store.dispatch(bleActions.writeBundleFailure({ error: err instanceof Error ? err.message : undefined }));
      throw err;
    }
  };

  public readonly deleteBundle = async (bundleId: string) => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to delete a bundle while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }
      store.dispatch(bleActions.deleteBundleStart());
      const bundleFileInfo = selectBleBundleFiles(store.getState()).find(i => i.bundleId === bundleId);
      if (!bundleFileInfo) throw new Error(`No bundle on controller for id ${bundleId}!`);

      await this.closeControllerFileSystem();
      const dirFiles = (await this.readDirectory(bundleFileInfo.dirPath)).filter(
        i => i.fileName !== BUNDLE_INFO_FILENAME,
      ); // A list of bundle files in the site directory.
      const isLastBundleInDir = dirFiles.every(file => file.fullPath === bundleFileInfo.fullPath);

      await this.slink.getFUnlink(bundleFileInfo.fullPath, 1000); // Delete the bundle file.
      if (isLastBundleInDir) {
        // If we're deleting the last bundle in a directory, also delete the info.txt (may or may not exist).
        try {
          await this.slink.getFUnlink(`${bundleFileInfo.dirPath}/${BUNDLE_INFO_FILENAME}`, 1000);
        } catch {}
        // If we're deleting the last bundle in a directory, also delete the directory.
        await this.slink.getFUnlink(bundleFileInfo.dirPath, 1000);
      }

      store.dispatch(bleActions.deleteBundleSuccess({ bundleId }));
    } catch (err) {
      console.error('Could not delete bundle from controller:', err);
      store.dispatch(bleActions.deleteBundleFailure({ error: err instanceof Error ? err.message : undefined }));
      throw err;
    }
  };

  public readonly updateFirmware = async (selectedFirmware: Firmware) => {
    try {
      if (this.lock) {
        console.warn(
          'Tried to update firmware of the controller device while another process held the lock. This likely represents a timing issue in the consuming code.',
        );
        return;
      }

      if (!selectedFirmware?._id) {
        throw new Error('No firmware was selected');
      }

      const controllerId = selectBleConnectedDevice(store.getState())?.serialNumber;
      if (!controllerId) {
        throw new Error('No serial number for connected device in state!');
      }

      store.dispatch(bleActions.updateFirmwareStart());

      let firmwareData;
      try {
        firmwareData = await getFirmwareFile(selectedFirmware._id, controllerId);
      } catch (err: any) {
        // If error is specifically license related, dispatch more precise error code
        if (axios.isAxiosError(err)) {
          const typedError = err as AxiosError<any>;
          if (typedError.response?.data?.errorCode === ErrorCode.InsufficientLicenses) {
            store.dispatch(
              bleActions.updateFirmwareFailure({
                error: err instanceof Error ? err.message : undefined,
                errorCode: 'assigningFirmwareLicense',
              }),
            );
            return;
          }
        }

        // Dispatch generic fetching firmware error code.
        store.dispatch(
          bleActions.updateFirmwareFailure({
            error: err instanceof Error ? err.message : undefined,
            errorCode: 'fetchingFirmware',
          }),
        );
        return;
      }

      const totalBytes = firmwareData.length;
      const filename = `${selectedFirmware.fileName}.${selectedFirmware.fileType}`;
      const filePath = `/bin/cbe/${filename}`;

      await this.closeControllerFileSystem();
      await this.slink.getFChangeDirectory('/', 1000); // Start at root directory
      try {
        await this.slink.getFMakeDirectory('bin', 1000); // Create /log
      } catch {}
      await this.slink.getFChangeDirectory('bin', 1000); // cd log
      try {
        await this.slink.getFMakeDirectory('cbe', 1000); // create /log/cbe
      } catch {}
      await this.slink.getFChangeDirectory('/', 1000); // Go back to root

      await this.writeFile('/bin/cbe/load_fw.txt', filePath); // Create or overwrite the load_fw.txt file with the path of the firmware file.

      await this.writeFile(filePath, firmwareData, writtenBytes => {
        store.dispatch(bleActions.updateFirmwareProgress({ writtenBytes, totalBytes }));
      });

      await this.slink.getResetSystem();

      await updateController(controllerId, { fw: selectedFirmware._id });
      store.dispatch(bleActions.updateFirmwareSuccess());
    } catch (err) {
      console.error('Error updating FW ', err);
      store.dispatch(
        bleActions.updateFirmwareFailure({
          error: err instanceof Error ? err.message : undefined,
          errorCode: 'installingFirmware',
        }),
      );
    }
  };

  /**
   * Writes a file to the device, overwriting it if it exists.
   * @param filePath The full path to the file on disk.
   * @param data The data to write.
   * @param onProgress A callback that is invoked with the number of bytes that have been written.
   * @throws If anything at all goes wrong.
   */
  private readonly writeFile = async (
    filePath: string,
    data: string | Uint8Array,
    onProgress?: (writtenBytes: number) => void,
  ) => {
    await this.slink.getFOpen(fatfs.fAccess.CreateAlways | fatfs.fAccess.Append, filePath, 1000);
    try {
      for (let i = 0; i < data.length; i += BleControllerService.WRITE_PACKET_SIZE) {
        const result = await this.slink.getFWrite(
          data.slice(i, Math.min(i + BleControllerService.WRITE_PACKET_SIZE, data.length)),
          5000,
        );
        if (result !== fatfs.fResult.Ok) throw new Error(`Write operation failed with error code ${result}`);
        onProgress?.(i + BleControllerService.WRITE_PACKET_SIZE);
      }
      await this.slink.getFEoF(1000);
    } finally {
      await this.slink.getFClose(1000);
    }
  };

  /**
   * Reads a file from the device.
   * @param filePath The full path of the file to read.
   * @param onProgress A callback that is invoked with the number of bytes that have been read.
   * @returns A promise that resolves to the file contents.
   * @throws If anything at all goes wrong.
   */
  private readonly readFile = async (
    filePath: string,
    onProgress?: (readBytes: number, totalBytes: number) => void,
  ) => {
    await this.slink.getFOpen(fatfs.fAccess.Read, filePath, 1000);
    const logFileLength = Number(await this.slink.getFGetSize(1000));
    await this.slink.getFClose(1000);

    await this.slink.getFOpen(fatfs.fAccess.Read, filePath, 1000);

    const chunkSize = 2048;
    let file = '';

    const iterations = Math.ceil(logFileLength / chunkSize);
    const decoder = new TextDecoder('ascii');

    try {
      for (let iterator = 1; iterator <= iterations; iterator++) {
        const readFile = (await this.slink.getFRead(chunkSize, 2000)) as { result: number; data: Uint8Array };

        if (readFile.result !== 0) throw new Error(`FRead failed with code ${readFile.result}`);

        file += decoder.decode(readFile.data.slice(2), { stream: true });

        // Exit case
        if (!readFile.data.length || readFile.data.length < chunkSize) break;
        onProgress?.(chunkSize * iterator, logFileLength);
      }
    } finally {
      file += decoder.decode();
      await this.slink.getFClose(1000);
    }

    onProgress?.(logFileLength, logFileLength);
    return file;
  };

  /**
   * Reads all metadata from the provided directory. Note that this changes the current open directory on the device!
   * @param path The full path of the directory to read file metadata from.
   * @param recursive Whether or not to recurse into subdirectories.
   * @throws If the directory cannot be read.
   */
  private readonly readDirectory = async (path: string, recursive = false): Promise<BleControllerFile[]> => {
    const directories: string[] = [path.replace(/\/$/, '')];
    const files: BleControllerFile[] = [];
    while (directories.length > 0) {
      const currentDirectory = directories.shift();
      if (!currentDirectory) break;

      await this.slink.getFOpenDirectory(currentDirectory, 1000); // Open directory for reading
      try {
        while (true) {
          try {
            const parsed = (await this.slink.getFReadDirectory(1000)) as ControllerReadDirResponse;
            // It appears slink parser is forcing null results into a valid read with null properties. The third condition accounts for this.
            if (!parsed || parsed.result !== 0 || !parsed.info.filePath) {
              throw new Error('No file information received, assuming end of list');
            }

            const fullPath = `${currentDirectory}/${parsed.info.filePath}`;
            // If the entry is a directory, don't include it in the results
            if ((parsed.info.attributes & 16) > 0) {
              if (recursive) {
                directories.push(fullPath);
              }
            } else {
              files.push({
                fileSize: parsed.info.fileSize,
                fileName: parsed.info.filePath,
                dirPath: currentDirectory,
                fullPath,
                createdAt:
                  typeof parsed.info.date === 'string'
                    ? parsed.info.date
                    : parsed.info.date instanceof Date
                      ? parsed.info.date.toISOString()
                      : new Date().toISOString(),
              });
            }
          } catch {
            // If error, break out of for while and try to push parsed logs to store.
            break;
          }
        }
      } finally {
        await this.slink.getFCloseDirectory(1000); // Close directory for reading
      }
    }
    return files;
  };

  /** Handles disconnection from the connected device. */
  public readonly close = async () => {
    try {
      if (this.activeLogService) this.activeLogService.pause();
    } catch {}
    try {
      await BleClient.stopNotifications(this.device.deviceId, bleIds.uartService, bleIds.rx);
    } catch {}
    try {
      this.slink.removeAllListeners();
    } catch {}
  };

  /** Called when the controller indicates that a log file stream has begun. */
  private readonly onStreamStart = (event: StartStreamResponse_t) => {
    const canLiveStream = selectBleCanLiveStreamFromController(store.getState());
    // If user can't live stream from this controller, do nothing.
    if (!canLiveStream(event.payload_data.serial)) {
      console.log(
        "[BleControllerService] Got a stream start event, but the current user can't stream from the controller",
        event.payload_data.serial,
      );
      event.streamStatus(StreamStatus_t.closed);
      return;
    }

    const filenameExtensionStartPosition = event.payload_data.file.lastIndexOf('.');
    const filenameOnly = event.payload_data.file.substring(0, filenameExtensionStartPosition);

    // Comment out if statement below when testing using "FOREMAN TEST" on controller
    if (this.isProbablyAnEncodedDateTime(filenameOnly)) {
      console.log(
        '[BleControllerService] Got a stream start filename that looks like a recent timestamp, ignored.',
        event.payload_data.file,
      );
      event.streamStatus(StreamStatus_t.closed);
      return;
    }

    store.dispatch(logActions.liveJobLogStreamBegin({ filename: event.payload_data.file }));
    let liveLog = this.getJobLogService(event.payload_data.file);
    // If no live log currently exists for the file, create a new service for it
    if (!liveLog) {
      liveLog = this.addJobLogService(event.payload_data.file, event.payload_data.serial);
    }
    liveLog.onStreamStart(event);
  };

  private readonly isProbablyAnEncodedDateTime = (possibleTimestamp: string) => {
    const re = new RegExp('^[0-9]{12}$');

    return re.test(possibleTimestamp);
  };

  /** Called when the controller sends a line for the currently streaming log. */
  private readonly onStreamData = (event: StreamResponse_t) => {
    if (!this.activeLogService) return;
    this.activeLogService.onStreamData(event);
  };

  /** Called when the controller requests that the application send it the current live stream status. */
  private readonly onStreamStatus = (event: StreamStatusResponse_t) => {
    if (!this.activeLogService) return;
    this.activeLogService.onStreamStatus(event);
  };

  /** Calls fclose on the controller file system and silences any exceptions from the operation. */
  private readonly closeControllerFileSystem = async () => {
    try {
      await this.slink.getFClose(1000);
    } catch {}
  };
}

if (module.hot) {
  module.hot.dispose(() => {
    BleClient.stopEnabledNotifications().catch(err => console.error('Error responding to onStreamStatus: ', err));
  });
  // This can't be hot-reloaded, trigger a full refresh
  module.hot.decline();
}
